import React, { useState } from "react";
import { ARIAKE, SITE } from "../lib/data";
import { Check, Maximize2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";

const waLink = (model) =>
  `https://wa.me/${SITE.whatsappIntl}?text=${encodeURIComponent(
    `Hola, me interesa la ${model} en Ariake.`
  )}`;

export default function Tipologias() {
  const [active, setActive] = useState(0);
  const t = ARIAKE.typologies[active];

  return (
    <section
      id="tipologias"
      data-testid="tipologias-section"
      className="bg-[hsl(var(--hh-bone))] py-20 md:py-28 lg:py-32 border-t border-black/5"
    >
      <div className="container-hh">
        <div className="reveal max-w-2xl">
          <div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-6">
            <span className="accent-line" />
            <span className="overline text-[hsl(var(--hh-moss))]" style={{ fontSize: "0.65rem" }}>
              Tipologías
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-balance">
            Cuatro maneras de{" "}
            <em style={{ fontStyle: "italic", color: "hsl(var(--hh-moss))" }}>
              llegar a casa.
            </em>
          </h2>
        </div>

        {/* Tabs */}
        <div className="mt-10 md:mt-12 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          {ARIAKE.typologies.map((tt, i) => (
            <button
              key={tt.id}
              onClick={() => setActive(i)}
              data-testid={`tipologia-tab-${tt.id}`}
              className={`text-left p-4 sm:p-5 border transition-all ${
                active === i
                  ? "bg-black text-white border-black"
                  : "bg-white text-black border-black/10 hover:border-black/40"
              }`}
            >
              <div className={`overline ${active === i ? "text-white/60" : "text-[hsl(var(--hh-moss))]"}`} style={{ fontSize: "0.55rem" }}>
                Tipo {tt.id}
              </div>
              <div className="font-display text-lg sm:text-xl md:text-2xl mt-1">{tt.area}</div>
              <div className={`text-[0.7rem] sm:text-xs mt-1 ${active === i ? "text-white/60" : "text-black/60"}`}>
                {tt.beds}
              </div>
            </button>
          ))}
        </div>

        {/* Active typology — plan + summary */}
        <div className="mt-8 md:mt-10 grid grid-cols-12 gap-x-4 gap-y-8 md:gap-10 items-start">
          <Dialog>
            <DialogTrigger asChild>
              <button
                className="col-span-12 md:col-span-7 relative group block bg-white border border-black/10 overflow-hidden"
                data-testid={`tipologia-plan-${t.id}`}
              >
                <div className="aspect-[16/10] relative">
                  <img
                    src={t.plan}
                    alt={`Plano ${t.name}`}
                    className="absolute inset-0 h-full w-full object-contain bg-white p-4"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-black overline px-4 py-2 flex items-center gap-2 border border-black" style={{ fontSize: "0.6rem" }}>
                      <Maximize2 size={12} /> Plano completo
                    </span>
                  </div>
                </div>
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl p-0 bg-white">
              <DialogTitle className="sr-only">Plano {t.name}</DialogTitle>
              <DialogDescription className="sr-only">
                Plano arquitectónico de la {t.name} en Ariake.
              </DialogDescription>
              <img src={t.plan} alt={`Plano ${t.name}`} className="w-full h-auto bg-white" />
            </DialogContent>
          </Dialog>

          <div className="col-span-12 md:col-span-5 reveal">
            <div className="overline text-[hsl(var(--hh-moss))]" style={{ fontSize: "0.6rem" }}>
              Tipo {t.id}
            </div>
            <h3 className="font-display text-3xl md:text-4xl mt-1">{t.name}</h3>

            <div className="grid grid-cols-3 gap-3 mt-6 mb-6 border-y border-black/10 py-4">
              <div>
                <div className="font-display text-xl md:text-2xl">{t.area}</div>
                <div className="overline text-[hsl(var(--hh-moss))] mt-1" style={{ fontSize: "0.52rem" }}>Área</div>
              </div>
              <div>
                <div className="font-display text-xl md:text-2xl">{t.beds}</div>
                <div className="overline text-[hsl(var(--hh-moss))] mt-1" style={{ fontSize: "0.52rem" }}>Dorm.</div>
              </div>
              <div>
                <div className="font-display text-xl md:text-2xl">{t.baths}</div>
                <div className="overline text-[hsl(var(--hh-moss))] mt-1" style={{ fontSize: "0.52rem" }}>Baños</div>
              </div>
            </div>

            <ul className="space-y-2 md:space-y-2.5" data-testid={`tipologia-detail-${t.id}`}>
              {t.detail.slice(0, 5).map((d, i) => (
                <li key={i} className="flex items-start gap-3 text-[hsl(0,0%,22%)] text-sm">
                  <Check size={14} className="mt-1 shrink-0 text-[hsl(var(--hh-moss))]" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-wrap gap-2 sm:gap-3">
              <a href="#contacto" className="btn-hh btn-hh-outline" data-testid={`tipologia-${t.id}-cta`}>
                Consultar
              </a>
              <a
                href={waLink(t.name)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hh btn-hh-outline"
              >
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
